import React, { useCallback } from 'react';
import {
  Page,
  Text,
  Card,
  BlockStack,
  Box,
  InlineStack,
  Button,
} from "@shopify/polaris";
import PageTitleBar from '../components/PageTitleBar';
import { useSafeAppBridge } from '../hooks/useSafeAppBridge';
import { useEnsureInstall } from '../hooks/useEnsureInstall';
import { Redirect } from '@shopify/app-bridge/actions';
import { useEffect } from 'react';
import { SHOPIFY_API_KEY, buildNodeApiEntryUrl } from '../config/env';

// Skeleton component for shimmer effect
const SkeletonBox = ({ width, height, borderRadius = '4px', style = {} }) => {
  const shimmerStyle = {
    width: width || '100%',
    height: height || '20px',
    backgroundColor: '#e5e7eb',
    borderRadius,
    position: 'relative',
    overflow: 'hidden',
    ...style,
  };

  const shimmerAnimation = {
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)',
    animation: 'shimmer 1.5s infinite',
  };

  return (
    <div style={shimmerStyle}>
      <div />
    </div>
  );
};

// Main Widget Skeleton Preview
const MainWidgetSkeleton = () => (
  <Box
    style={{
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      overflow: 'hidden',
      backgroundColor: '#ffffff',
    }}
  >
    <Box padding="400">
      <BlockStack gap="300">
        <InlineStack gap="300" align="start" wrap={false}>
          {/* Banner Image Skeleton - Compact */}
          <Box style={{ flex: '0 0 120px', minWidth: 0 }}>
            <SkeletonBox height="120px" borderRadius="8px" />
          </Box>
          
          {/* Content */}
          <Box style={{ flex: '1', minWidth: 0 }}>
            <BlockStack gap="200">
              {/* Title Skeleton */}
              <SkeletonBox width="80%" height="20px" />
              
              {/* Description Skeleton */}
              <BlockStack gap="100">
                <SkeletonBox width="100%" height="14px" />
                <SkeletonBox width="70%" height="14px" />
              </BlockStack>
              
              {/* Progress Bar Skeleton */}
              <Box paddingBlockStart="200">
                <SkeletonBox width="100%" height="6px" borderRadius="3px" />
                <Box paddingBlockStart="100">
                  <SkeletonBox width="40%" height="12px" />
                </Box>
              </Box>
            </BlockStack>
          </Box>
        </InlineStack>
        
        {/* Button Skeleton */}
        <Box paddingBlockStart="300">
          <SkeletonBox width="100%" height="36px" borderRadius="6px" />
        </Box>
      </BlockStack>
    </Box>
  </Box>
);

// Widget configuration
const WIDGET_CONFIG = {
  id: 'main',
  blockHandle: 'campaign_main',
  title: 'Campaign Widget',
  description: 'Configure and customize your campaign widget settings. This widget includes both the main campaign display and reward widgets for crowdfunding campaigns.',
  buttonLabel: 'Manage Campaign Widget'
};

export default function Widget() {
  const app = useSafeAppBridge();

  // Get install state from Redux (using reusable hook - ensures install API is only called once)
  const {
    installChecked,
    installSuccess,
    installLoading,
    installData,
    shop,
    host
  } = useEnsureInstall();

  // Generate theme editor URL for app block deep linking - Dynamic function
  // MUST be called before any early returns to follow React hooks rules
  const getThemeEditorUrl = useCallback((blockHandle, target = 'mainSection', template = 'product') => {
    if (!shop) {
      console.warn('[Widget] Cannot generate theme editor URL: shop missing', { shop });
      return null;
    }
    
    // Extract shop name from shop domain (e.g., "myshop.myshopify.com" -> "myshop")
    // Handle both formats: "myshop.myshopify.com" or "myshop"
    const shopName = shop.includes('.myshopify.com') 
      ? shop.replace('.myshopify.com', '') 
      : shop;
    
    // Get app API key from config
    const apiKey = SHOPIFY_API_KEY;
    
    if (!apiKey) {
      console.warn('[Widget] Cannot generate theme editor URL: app API key missing');
      return null;
    }
    
    if (!blockHandle) {
      console.warn('[Widget] Cannot generate theme editor URL: block handle missing');
      return null;
    }
    
    // Generate deep linking URL for app blocks
    // Format: https://{shop-name}.myshopify.com/admin/themes/current/editor?addAppBlockId={api_key}/{handle}&target={target}&template={template}
    return `https://${shopName}.myshopify.com/admin/themes/current/editor?addAppBlockId=${apiKey}/${blockHandle}&target=${target}&template=${template}`;
  }, [shop]);

  // Handle redirect to theme editor - Dynamic handler for any widget
  // MUST be called before any early returns to follow React hooks rules
  const handleOpenThemeEditor = useCallback((blockHandle) => {
    if (!blockHandle) {
      console.error('[Widget] Block handle is required');
      return;
    }
    
    // Generate theme editor URL
    const themeEditorUrl = getThemeEditorUrl(blockHandle);
    if (!themeEditorUrl) {
      console.error('[Widget] Failed to generate theme editor URL');
      return;
    }
    
    // Try App Bridge redirect first
    if (app) {
      try {
        const redirect = Redirect.create(app);
        redirect.dispatch(Redirect.Action.REMOTE, {
          url: themeEditorUrl,
          newContext: true
        });
        return;
      } catch (error) {
        console.warn('[Widget] App Bridge redirect failed, using window.open:', error);
      }
    }
    
    // Fallback: open in new tab
    window.open(themeEditorUrl, '_blank');
  }, [getThemeEditorUrl, app]);

  // Redirect on install failure (no HTML shown)
  useEffect(() => {
    if (installChecked && !installSuccess && !installData && shop) {
      const redirectUrl = buildNodeApiEntryUrl(shop, host);

      // Try App Bridge redirect first
      if (app) {
        try {
          const redirect = Redirect.create(app);
          redirect.dispatch(Redirect.Action.REMOTE, redirectUrl);
          return;
        } catch (error) {
          console.warn('[Widget] App Bridge redirect failed, trying fallback');
        }
      }

      // Fallback: window navigation
      try {
        if (window.top && window.top !== window) {
          window.top.location.href = redirectUrl;
        } else {
          window.location.href = redirectUrl;
        }
      } catch (error) {
        window.open(redirectUrl, '_blank');
      }
    }
  }, [installChecked, installSuccess, installData, shop, host, app]);

  // OPTIMIZATION: If install data already exists in Redux, use it immediately
  // Don't wait for installLoading if data is already available
  // The checkInstall thunk will skip API call if data exists
  if (installChecked && !installSuccess && !installData) {
    return null;
  }

  // If install data exists, render immediately (don't wait for loading)
  // If no data exists, wait for install check to complete
  if (!installData) {
    if (!installChecked || installLoading) {
      return null;
    }
  }

  return (
    <>
      {/* Shimmer animation style - only add once */}
      <style>{`
        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }
      `}</style>
      <PageTitleBar
        title="Widget"
        // subtitle="Manage your campaign widgets"
      />
      <Page>
        <BlockStack gap="500">
          {/* Single Widget Layout - Centered */}
          <Box style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
            <Card>
              <Box padding="500">
                <BlockStack gap="400">
                  <InlineStack gap="500" align="start" wrap={false}>
                    {/* Left Side - Skeleton Preview */}
                    <Box style={{ flex: '0 0 50%', minWidth: 0 }}>
                      <MainWidgetSkeleton />
                    </Box>

                    {/* Right Side - Description */}
                    <Box style={{ flex: '1', minWidth: 0 }}>
                      <BlockStack gap="300">
                        <Text variant="headingMd" as="h2">
                          {WIDGET_CONFIG.title}
                        </Text>
                        <Text variant="bodyMd" tone="subdued">
                          {WIDGET_CONFIG.description}
                        </Text>
                        <Button 
                          variant="primary"
                          onClick={() => handleOpenThemeEditor(WIDGET_CONFIG.blockHandle)}
                        >
                          {WIDGET_CONFIG.buttonLabel}
                        </Button>
                      </BlockStack>
                    </Box>
                  </InlineStack>
                </BlockStack>
              </Box>
            </Card>
          </Box>
        </BlockStack>
      </Page>
    </>
  );
}

